import * as React from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import { ThemePalette } from 'mui.theme';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      minWidth: 300,
      borderRadius: 8,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    },
  },
  // Using proper types for MUI positioning
  anchorOrigin: {
    vertical: 'bottom' as const,
    horizontal: 'left' as const,
  },
  transformOrigin: {
    vertical: 'top' as const,
    horizontal: 'left' as const,
  },
};

interface MultiSelectProps {
  options: string[];
  handleChange: (options: string[]) => void;
  selectedOptions: string[];
  label: string;
  darkMode?: boolean;
  colorMap?: Record<string, string>;
  width?: number | string;
}

// Default color map for activities
const defaultActivityColors: Record<string, string> = {
  Bolt: '#3F51B5',
  Drill: '#FF9800',
  Bucket: '#FFC107',
  'Battery Energy Drained': '#f2765d',
  'Battery Accumulated Energy': '#f2af11',
  'Battery Charge Status': '#81d4fa',
};

export default function MultipleSelectCheckmarks({
  options,
  handleChange,
  selectedOptions,
  label,
  darkMode = false,
  colorMap,
  width = 320,
}: MultiSelectProps) {
  // const [personName, setPersonName] = React.useState<string[]>([]);

  const onChange = (event: SelectChangeEvent<typeof selectedOptions>) => {
    const {
      target: { value },
    } = event;
    if (value?.includes('all')) {
      if (selectedOptions?.length === options?.length)
        handleChange([options[0]]);
      else handleChange(options);
      return;
    } else if (selectedOptions?.length === options?.length) {
      handleChange([]);
    }
    handleChange(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value
    );
  };

  // Use provided colorMap or default to activity colors
  const activeColorMap = colorMap ?? defaultActivityColors;

  return (
    <FormControl sx={{ width }} size="small">
      <InputLabel
        id={`${label}-label`}
        sx={{
          color: darkMode ? '#fff' : '#000',
          '&.Mui-focused': {
            color: darkMode ? '#fff' : ThemePalette.typography.primary,
          },
          fontWeight: 500,
        }}
      >
        {label}
      </InputLabel>
      <Select
        labelId={`${label}-label`}
        id={`${label}-select`}
        multiple
        value={selectedOptions}
        onChange={onChange}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => {
          // If all options are selected, show "All"
          if (selected.length === options.length) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    color: darkMode ? '#fff' : ThemePalette.typography.primary,
                  }}
                >
                  All ({options.length})
                </Box>
              </Box>
            );
          }

          // If too many items are selected, show count instead of all chips
          if (selected.length > 1) {
            return (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '24px',
                }}
              >
                <Chip
                  size="small"
                  label={`${selected.length} selected`}
                  sx={{
                    backgroundColor: darkMode ? '#2c3e50' : '#e0e0e0',
                    color: darkMode ? '#fff' : '#000',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: 24,
                    '& .MuiChip-label': { px: 1 },
                    borderRadius: '4px',
                  }}
                />
              </Box>
            );
          }

          // Show chips for 1-2 selected items
          return (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                maxWidth: '100%',
                overflow: 'hidden',
                height: '24px',
              }}
            >
              {selected.map((value) => (
                <Chip
                  key={value}
                  label={value}
                  size="small"
                  sx={{
                    backgroundColor:
                      activeColorMap[value] ??
                      (darkMode ? '#2c3e50' : '#e0e0e0'),
                    color: activeColorMap[value]
                      ? '#fff'
                      : darkMode
                      ? '#fff'
                      : '#000',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: 24,
                    '& .MuiChip-label': { px: 1 },
                    borderRadius: '4px',
                  }}
                />
              ))}
            </Box>
          );
        }}
        sx={{
          width,
          color: darkMode ? '#fff' : '#000',
          backgroundColor: darkMode
            ? ThemePalette.dark.menuOptions
            : ThemePalette.light.menuOptions,
          borderRadius: '8px',
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: darkMode
              ? ThemePalette.dark.cartesianGrid
              : ThemePalette.light.cartesianGrid,
            borderRadius: '8px',
            borderWidth: '1px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: darkMode
              ? ThemePalette.dark.conventions.fleetAverage
              : ThemePalette.light.conventions.fleetAverage,
            borderWidth: '1.5px',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: darkMode
              ? ThemePalette.dark.conventions.fleetAverage
              : ThemePalette.light.conventions.fleetAverage,
            borderWidth: '1.5px',
          },
          '.MuiSvgIcon-root': {
            color: darkMode ? '#fff' : '#000',
          },
          '& .MuiSelect-select': {
            padding: '8px 14px',
            minHeight: '24px',
            display: 'flex',
            alignItems: 'center',
          },
          boxShadow: darkMode
            ? '0 2px 8px rgba(0,0,0,0.2)'
            : '0 1px 3px rgba(0,0,0,0.1)',
        }}
        MenuProps={{
          ...MenuProps,
          PaperProps: {
            ...MenuProps.PaperProps,
            sx: {
              bgcolor: darkMode
                ? ThemePalette.dark.menuOptions
                : ThemePalette.light.menuOptions,
              color: darkMode ? '#fff' : '#000',
              borderRadius: '8px',
              padding: '4px 0',
              minWidth: width,
              maxWidth: width,
              '& .MuiMenuItem-root': {
                padding: '6px 16px',
                borderRadius: '4px',
                margin: '2px 8px',
                '&:hover': {
                  backgroundColor: darkMode
                    ? 'rgba(255,255,255,0.08)'
                    : 'rgba(0,0,0,0.04)',
                },
              },
            },
          },
        }}
      >
        <MenuItem
          value={'all'}
          sx={{ borderRadius: '4px', minWidth: width, maxWidth: width }}
        >
          <Checkbox
            checked={selectedOptions?.length === options?.length}
            color="primary"
            size="small"
            sx={{
              color: darkMode ? '#fff' : ThemePalette.typography.primary,
              '&.Mui-checked': {
                color: darkMode
                  ? ThemePalette.dark.conventions.fleetAverage
                  : ThemePalette.typography.primary,
              },
            }}
          />
          <ListItemText
            primary={`All (${options?.length})`}
            primaryTypographyProps={{
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          />
        </MenuItem>
        {options.map((name) => (
          <MenuItem
            key={name}
            value={name}
            sx={{ borderRadius: '4px', minWidth: 300, maxWidth: 300 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Checkbox
                checked={selectedOptions.includes(name)}
                size="small"
                sx={{
                  color: darkMode ? '#fff' : ThemePalette.typography.primary,
                  '&.Mui-checked': {
                    color:
                      activeColorMap[name] ||
                      (darkMode
                        ? ThemePalette.dark.conventions.fleetAverage
                        : ThemePalette.typography.primary),
                  },
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {activeColorMap[name] && (
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '3px',
                      backgroundColor: activeColorMap[name],
                      flexShrink: 0,
                    }}
                  />
                )}
                <ListItemText
                  primary={name}
                  primaryTypographyProps={{
                    fontWeight: selectedOptions.includes(name) ? 600 : 400,
                    fontSize: '0.875rem',
                  }}
                />
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
